/**
 * stage4_fx — shader wave stage 4 (2026-09-09): the NEW engine shaders wired
 * into Atlas, the pure rules in one place for the harness:
 *
 *   cloud shadows      · shadow strength by the weather's cloud coverage
 *   rain ripples       · ripple intensity by the precipitation kind + density
 *   aurora             · which nights have one (seeded by the day), how
 *                        bright by the night factor
 *   bloom              · the tier's bloom filter
 *
 * The registries at the bottom are the runtime side (handle sets the
 * per-frame drivers write) and the counters `__stage4()` reports.
 */

export const STAGE4_FLAGS = ['cloudShadows', 'groundAntiTiling', 'rainRipples', 'aurora', 'nebulae', 'planetAtmosphere', 'underwaterShafts', 'dqSkinning'] as const;
export type Stage4Flag = (typeof STAGE4_FLAGS)[number];

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (v: number): number => { const t = clamp01(v); return t * t * (3 - 2 * t); };

/** Cloud-shadow darkness 0..0.6 by the global coverage: none under a clear sky, full under an overcast. */
export function cloudShadowStrength(coverage: number): number {
  return 0.6 * smooth((clamp01(coverage) - 0.15) / 0.55);
}

/** The cloud field's own lattice tilt (CloudRenderer rotates its weather UV ~27° off the world axes). */
export const CLOUD_FIELD_ROTATION = Math.atan2(0.454, 0.891);
/** Metres per weather-map repeat (CloudConfig weatherScale 0.0001). */
export const CLOUD_FIELD_SCALE_M = 10_000;

/** Rain ripple intensity 0..1: rain only (snow and ash leave no rings), by particle density. */
export function rainRippleIntensity(kind: string, density: number): number {
  if (kind !== 'rain' && kind !== 'storm') return 0;
  return clamp01(density);
}

/** Does this world day carry an aurora? ~35 % of nights, seeded (the same for every client). */
export function auroraTonight(dayIndex: number, seed: number): boolean {
  let h = (Math.floor(dayIndex) * 374761393 + Math.floor(seed) * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return ((h >>> 0) / 4294967296) < 0.35;
}

/** Aurora brightness by the night factor: nothing before deep dusk, full at night. */
export function auroraIntensity(night: number, tonight: boolean, strength = 1): number {
  if (!tonight) return 0;
  return strength * smooth((clamp01(night) - 0.35) / 0.4);
}

/** The world day index from the sky's continuous day count. */
export function dayIndexOf(dayCount: number): number { return Math.floor(dayCount); }

// ── Runtime registries ────────────────────────────────────────────────────
export interface CloudShadowHandleLike { offset: { value: { set(x: number, y: number): unknown } }; strength: { value: number } }
export const cloudShadowHandles = new Set<CloudShadowHandleLike>();
export interface RainHandleLike { intensity: { value: number } }
export const rainHandles = new Set<RainHandleLike>();

export const stage4Stats = {
  cloudShadowMats: 0,   // materials carrying the cloud-shadow layer
  cloudShadowInLight: 0, // of those, the ones shadowing the LIGHT (receivedShadowNode) rather than the albedo
  cloudShadowStrength: 0,
  cloudSource: 'none' as 'none' | 'clouds' | 'baked',
  stochasticGround: 0,  // ground materials sampling stochastically
  rainMats: 0,
  rainIntensity: 0,
  auroraBuilt: 0,
  auroraVisible: false,
  auroraIntensity: 0,
  nebulae: 0,
  atmospheres: 0,
  shaftsBuilt: 0,
  shaftsVisible: false,
  dqActors: 0,
};
